// Hasil reviu of Review Dokumen: the status, the summary and the findings
// grouped by kategori, in the presentation of the activity review
// (project/ActivityDocs.tsx, classes ad-review-* and ad-finding*).
import type { DocReview, ReviewFinding } from "../api";
import { formatDateTime } from "../lib/format";
import { Badge, EmptyState } from "../ui/components";
import { Glyph } from "../ui/icons";
import { CATEGORIES, DOC_TYPE_LABEL, LEVEL_LABEL, LEVEL_ORDER, LEVEL_TONE, STATUS_LABEL, STATUS_TONE } from "./labels";

/** Pluto's review is a check aid; the decision stays with the official. */
export const REVIEW_HINT = "Hasil review Pluto membantu pemeriksaan. Keputusan akhir tetap pada pejabat yang berwenang.";

export function groupFindings(findings: ReviewFinding[]) {
  return CATEGORIES.map((c) => ({
    ...c,
    findings: findings.filter((f) => f.kategori === c.id).sort((a, b) => LEVEL_ORDER.indexOf(a.tingkat) - LEVEL_ORDER.indexOf(b.tingkat)),
  })).filter((g) => g.findings.length > 0);
}

export function ReviewResult({
  review,
  busy,
  onDownload,
  onDelete,
  onClose,
}: {
  review: DocReview;
  /** Unduh or Hapus is running. */
  busy: boolean;
  onDownload: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const groups = groupFindings(review.findings);
  return (
    <section className="card dr-result" aria-labelledby="dr-result-heading">
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="dr-result-heading" className="card-title">
            Hasil review
          </h2>
          <p className="section-lead">{review.title}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onClose} aria-label="Tutup hasil review">
          <Glyph name="close" />
          <span>Tutup</span>
        </button>
      </div>
      <div className="ad-review-head">
        <p className="ad-panel-meta">
          <Badge tone={STATUS_TONE[review.status]}>{STATUS_LABEL[review.status]}</Badge>
          <Badge>{DOC_TYPE_LABEL[review.docType]}</Badge>
          <span>Direviu {formatDateTime(review.createdAt)}</span>
        </p>
        <p className="ad-panel-meta">
          {review.projectName ? <span>Project: {review.projectName}</span> : null}
          {review.fileName ? <span>File: {review.fileName}</span> : <span>Teks yang ditempel</span>}
        </p>
        <p className="ad-review-summary">{review.summary}</p>
        {review.instructions ? (
          <p className="ad-finding-detail">
            <strong>Arahan:</strong> {review.instructions}
          </p>
        ) : null}
        {review.truncated ? <p className="form-note">Dokumen panjang: Pluto membaca bagian awalnya saja.</p> : null}
        <div className="button-row">
          <button type="button" className="btn" disabled={busy} onClick={onDownload}>
            <Glyph name="documentDownload" />
            <span>Unduh hasil review</span>
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onDelete}>
            <Glyph name="trash" />
            <span>Hapus</span>
          </button>
        </div>
      </div>
      {groups.length === 0 ? (
        <EmptyState icon="tickCircle" title="Tidak ada temuan" body="Pluto tidak menemukan hal yang perlu diperbaiki pada dokumen ini." />
      ) : (
        groups.map((g) => (
          <section key={g.id} className="ad-review-group" aria-label={g.label}>
            <h3 className="ad-review-group-title">
              {g.label} <span className="ad-review-count">({g.findings.length})</span>
            </h3>
            <ul className="ad-findings">
              {g.findings.map((f, i) => (
                <li key={`${g.id}-${i}`} className="ad-finding">
                  <p className="ad-finding-meta">
                    <Badge tone={LEVEL_TONE[f.tingkat]}>{LEVEL_LABEL[f.tingkat]}</Badge>
                    <span className="ad-finding-section">{f.bagian ? `Bagian: ${f.bagian}` : "Seluruh dokumen"}</span>
                  </p>
                  <p className="ad-finding-text">{f.temuan}</p>
                  {f.saran ? (
                    <p className="ad-finding-detail">
                      <strong>Saran:</strong> {f.saran}
                    </p>
                  ) : null}
                  {f.rujukan ? (
                    <p className="ad-finding-detail">
                      <strong>Rujukan:</strong> {f.rujukan}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      <p className="form-note">{REVIEW_HINT}</p>
    </section>
  );
}
